import { createSignal, createMemo, onCleanup, onMount, Index, Show, type Accessor } from 'solid-js';
import { Portal } from 'solid-js/web';
import { timelineEditorOpen } from '../timeline/store.ts';
import { BPM_RANGE, clampBpm } from '../clock/bpmStorage.ts';
import { setPhaseOffset } from '../clock/phaseOffset.ts';
import {
  practiceIsPlaying, togglePracticePlaying, tapPracticeDownbeat,
  tapTempo, cyclePracticeSig, practiceSigNum, practiceSigDen, setPracticeBpm,
} from '../clock/practiceClock.ts';
import {
  loadOffset as loadTransportOffset,
  saveOffset as saveTransportOffset,
  clampOffset as clampTransportOffset,
} from '../clock/transportOffset.ts';
import { isEditMode, uiMode, toggleEditMode } from '../layout/store.ts';
import { performanceMode } from '../opbridge/performanceMode.ts';
import { navigateOpen, toggleNavigate } from '../navigate/store.ts';
import { navigateDocked, dockHidden, canvasActualSize, setCanvasActualSize } from '../navigate/dock.ts';
import { appUpdateReady } from '../appUpdate.ts';
import { canUndoAny, canRedoAny, globalUndo, globalRedo } from '../undo/history.ts';
import { OpbridgeBadge } from './OpbridgeBadge.tsx';
import { ClockSourcePill } from './ClockSourcePill.tsx';
import { TransportPill, transportState } from './TransportPill.tsx';
import { PagesPills } from './PagesPills.tsx';
import { loadFloaterPos, saveFloaterPos, type FloaterPos } from '../layout/transportFloaterPosition.ts';

interface TransportSnapshot {
  bar: number;
  beat: number;
  sixteenth: number;
  isPlaying: boolean;
  tempo: number;
  sigNum: number;
  sigDen: number;
  songTimeBeats: number;
  ts: number;
}

interface HeaderProps {
  bpm: Accessor<number>;
  /** 0..1 position within the bar (quantum=4). */
  phase: Accessor<number>;
  /** Continuous song-beat from smoothClock — drives the bar.beat.sixteenth
   *  counter. Anchored to Live's songTimeBeats when slaved, else the practice
   *  clock's own beat (0 = downbeat). */
  beat: Accessor<number>;
  /** Whether the bridge's clock is currently ticking. */
  isPlaying: Accessor<boolean>;
  /** Latest transport snapshot from the M4L OSC bridge (null = none yet). */
  transport: Accessor<TransportSnapshot | null>;
  /** Wall-clock tick so stale-checks re-render every second. */
  nowTick: Accessor<number>;
  /** True when the PWA is following Live's transport (M4L device is live).
   *  When set: BPM displays with 2 decimals, drag is disabled, and the cluster
   *  shows a "Live" cue instead of "drag". */
  isSlaved: Accessor<boolean>;
  /** True when the project does not fit this screen AND the operator has
   *  dismissed the bottom notice. The `!` chip is the way back to it - a
   *  dismissed warning must stay recoverable, or dismissing it is just a
   *  second way to get stranded. */
  fitAlert: Accessor<boolean>;
  /** Re-show the dismissed fit notice. */
  onShowFitNotice: () => void;
}

/** Stale threshold for the M4L transport readout. Beyond this, fall back to
 *  the manual phase strip. The M4L device emits ~15 Hz while playing AND on
 *  every change to is_playing/tempo/sig, so 2s of silence is unambiguous. */
const TRANSPORT_STALE_MS = 2000;

// Pixels-per-BPM-unit when dragging vertically. Higher = needs more travel for
// the same change. 4 felt right in testing (24px = 6 BPM, a normal "nudge").
const PX_PER_BPM = 4;

export function Header(props: HeaderProps) {
  // Transport live = a snapshot exists AND its ts is within the stale window.
  // `nowTick` recomputes once a second so the readout fades cleanly when Live's
  // M4L device gets closed or the master track is muted.
  const transportLive = () => {
    const t = props.transport();
    if (!t) return false;
    return props.nowTick() - t.ts < TRANSPORT_STALE_MS;
  };

  // Active time signature — Live's when a fresh transport is driving us, else
  // the practice clock's. Both the metronome dot count and the bar counter
  // read these, so the two can never disagree about the meter.
  const sigNumNow = () => {
    const t = props.transport();
    return transportLive() && t && t.sigNum > 0
      ? t.sigNum
      : Math.max(1, Math.round(practiceSigNum()));
  };
  const sigDenNow = () => {
    const t = props.transport();
    return transportLive() && t && t.sigDen > 0 ? t.sigDen : practiceSigDen();
  };

  // Phase-strip dots = beats per bar, so 3/4 shows 3, 7/8 shows 7. Drives BOTH
  // the practice strip and the live metronome (the live cluster is the same
  // shape, just read-only — Live owns the transport).
  const dotCount = () => Math.max(1, Math.round(sigNumNow()));
  // Held while the timeline editor covers the whole window (2026-10-20): these dots changed ~200 times a second
  // under it, and every change made the browser repaint and re-layerize the editor's thousands of elements too.
  const activeDot = createMemo<number>((prev) => (timelineEditorOpen() ? prev : Math.min(dotCount() - 1, Math.floor(props.phase() * dotCount()))), -1);

  // Idle pulse duration for the merged wordmark/metronome dot (2026-08-22).
  // Real BPM, not the static 500ms (=120bpm) design-token default — the
  // whole point of merging this dot with the metronome is that it's already
  // "the clock, just not started yet", so it should already read the real
  // tempo rather than an arbitrary ambient rate.
  const idleBeatMs = createMemo(() => {
    const b = props.bpm();
    return Number.isFinite(b) && b > 0 ? Math.round(60000 / b) : 500;
  });

  // ─── Transport visual offset ──────────────────────────────
  // LOM observer + UDP + WS + render add ~80-150ms of lag. The user drags
  // up/down on the bar counter to bias the display forward. Stored per device
  // in localStorage so laptop/iPad each have their own value. Only applied
  // when a live transport is driving us — in practice mode the local clock IS
  // the truth, so there is no latency to compensate for.
  const [transportOffsetMs, setTransportOffsetMs] = createSignal(loadTransportOffset());
  const [offsetDragging, setOffsetDragging] = createSignal(false);

  // ─── Bar counter (bar.beat.sixteenth) — the ONE timecode ───
  // Sole position readout (2026-07-28). It used to be one of two: a second
  // bar.beat.sixteenth lived in the metronome slot whenever a live transport
  // was fresh, and the pair DISAGREED — that one counted the sixteenth
  // beat-relative (1..4) off the 4 Hz poll snapshot, this one counts it
  // bar-relative (1..16) off smoothClock's 60fps interpolation, per DEC-093.
  // The live readout was retired; the metronome slot now shows the live
  // metronome (dots + play state + sig), the same shape it has in practice
  // mode, so the header no longer restructures when Live connects.
  //
  // Reads smoothClock's continuous beat + the active time signature. The
  // sixteenth counts across the whole bar (1..beats-per-bar × 4), so 4/4
  // reads "1.4.16" on the last sixteenth — matches the operator's spec.
  // Each field is rendered in a fixed-width column (CSS) so digits keep set
  // positions — a rolling sixteenth (9→10) never reflows the readout. That
  // static position is why this is the readout the operator kept.
  //
  // The visual offset is folded in HERE now (it used to apply only to the
  // retired readout, so keeping the other one would have silently dropped the
  // operator's latency calibration). offset_beats = offset_ms × bpm / 60000.
  const barPos = createMemo<{ bar: number; beat: number; sixteenth: number }>((prev) => {
    // Held while the timeline editor covers the window (as `activeDot`).
    if (timelineEditorOpen() && prev) return prev;
    const raw = props.beat();
    let beat = Number.isFinite(raw) && raw > 0 ? raw : 0;
    const live = transportLive();
    if (live) {
      const offMs = transportOffsetMs();
      if (offMs !== 0) {
        const bpm = props.bpm();
        const adjusted = beat + (offMs / 1000) * (bpm / 60);
        if (adjusted >= 0) beat = adjusted; // never show negative bars
      }
    }
    const sigNum = Math.max(1, Math.round(sigNumNow()));
    const sigDen = sigDenNow() > 0 ? sigDenNow() : 4;
    const beatsPerBar = sigNum * (4 / sigDen);          // in quarter-note beats
    const posInBar = beat % beatsPerBar;                // 0..beatsPerBar
    return {
      bar: Math.floor(beat / beatsPerBar) + 1,
      beat: Math.floor(posInBar) + 1,                   // 1..sigNum (for /4)
      sixteenth: Math.floor(posInBar * 4) + 1,          // 1..beatsPerBar×4
    };
  });
  // Per-field memos so each column's text node only updates when its own
  // value changes (not every animation frame).
  const barNum = createMemo(() => barPos().bar);
  const beatNum = createMemo(() => barPos().beat);
  const subNum = createMemo(() => barPos().sixteenth);

  function onOffsetPointerDown(ev: PointerEvent) {
    // Only meaningful against a live transport — see the barPos note.
    if (!transportLive()) return;
    ev.preventDefault();
    const startY = ev.clientY;
    const startOff = transportOffsetMs();
    let lastOff = startOff;
    setOffsetDragging(true);

    const target = ev.currentTarget as HTMLElement;
    target.setPointerCapture(ev.pointerId);

    const PX_PER_MS = 0.5; // 2 px = 1 ms, gentle precision
    const onMove = (e: PointerEvent) => {
      const deltaPx = startY - e.clientY;
      const next = clampTransportOffset(startOff + deltaPx / PX_PER_MS);
      if (next !== lastOff) {
        lastOff = next;
        setTransportOffsetMs(next);
        saveTransportOffset(next);
      }
    };
    const onUp = () => {
      setOffsetDragging(false);
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  }

  // ─── BPM drag ────────────────────────────────────────────
  const [dragging, setDragging] = createSignal(false);

  function onBpmPointerDown(ev: PointerEvent) {
    // Live owns the tempo when slaved — silently ignore drag attempts.
    // The button is also visually dimmed + retitled, so the user shouldn't
    // try to drag in the first place.
    if (props.isSlaved()) return;
    ev.preventDefault();
    const startY = ev.clientY;
    const startBpm = props.bpm();
    let lastBpm = startBpm;
    setDragging(true);

    // Capture the pointer so we keep getting move/up events even if the
    // pointer leaves the element (or the window briefly).
    const target = ev.currentTarget as HTMLElement;
    target.setPointerCapture(ev.pointerId);

    const onMove = (e: PointerEvent) => {
      // Up on screen = up in BPM. Pixels go down-positive in DOM, so negate.
      const deltaPx = startY - e.clientY;
      const next = clampBpm(startBpm + deltaPx / PX_PER_BPM);
      if (next !== lastBpm) {
        lastBpm = next;
        // Drives smoothClock via App's practice-clock effect (and persists).
        setPracticeBpm(next);
      }
    };
    const onUp = () => {
      setDragging(false);
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  }

  onCleanup(() => setDragging(false));

  // ─── Metronome tap → set THIS moment as beat 1 ────────────
  // The metronome (phase strip) is the beat-1 control. Tapping re-zeros the
  // practice clock's phase, and if the transport is stopped it also starts
  // it - so one tap both starts and aligns. Clears any leftover Link-era
  // phase offset so the playhead doesn't double-correct (in practice mode
  // the clock IS the truth). Wired to the local practice transport now, not
  // the retired legacy Node bridge.
  function onMetronomeTap() {
    tapPracticeDownbeat();
    setPhaseOffset(0);
  }

  // ─── Floating transport panel (DEC-170) ──────────────────────────
  // Only the dot cluster stays pinned in the bar. Everything interactive
  // about the transport - play/stop, TAP, sig, BPM drag, the offset chip -
  // lives in this panel, toggled by tapping the dots. Docks just under the
  // dot cluster by default (computed from ITS rect, not the panel's own -
  // the panel doesn't exist in the DOM until first opened); a grab handle
  // lets the operator drag it anywhere, remembered per-device thereafter.
  // Body-portalled + position:fixed at measured px, the same DEC-096
  // rect-anchored-popover exemption ItemContextMenu.tsx uses - not
  // shellPortal, because this is a measured-coordinate popover, not a
  // sized-in-%-of-the-shell modal.
  const [floaterOpen, setFloaterOpen] = createSignal(false);
  const [floaterPos, setFloaterPosState] = createSignal<FloaterPos | null>(loadFloaterPos());
  const [dockedPos, setDockedPos] = createSignal<FloaterPos>({ left: 0, top: 0 });
  let metroBtnRef: HTMLButtonElement | undefined;
  const effectiveFloaterPos = () => floaterPos() ?? dockedPos();

  function toggleFloater() {
    if (!floaterOpen() && floaterPos() === null && metroBtnRef) {
      const rect = metroBtnRef.getBoundingClientRect();
      setDockedPos({ left: Math.max(8, rect.left - 50), top: rect.bottom + 8 });
    }
    setFloaterOpen((v) => !v);
  }

  function onFloaterGrabPointerDown(ev: PointerEvent) {
    ev.preventDefault();
    const panelEl = (ev.currentTarget as HTMLElement).closest('.transport-floater') as HTMLElement | null;
    if (!panelEl) return;
    const rect = panelEl.getBoundingClientRect();
    const startX = ev.clientX;
    const startY = ev.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;
    const target = ev.currentTarget as HTMLElement;
    target.setPointerCapture(ev.pointerId);

    const onMove = (e: PointerEvent) => {
      setFloaterPosState({
        left: Math.max(4, startLeft + (e.clientX - startX)),
        top: Math.max(4, startTop + (e.clientY - startY)),
      });
    };
    const onUp = () => {
      const p = floaterPos();
      if (p) saveFloaterPos(p);
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  }

  // ─── Logo menu (full-screen modal) ──────────────────────────

  /**
   * ⚠️ WHAT SITS AGAINST THE WORDMARK IS THE BUILD, NOT A SCOPE VERSION.
   *
   * Until 2026-09-21 this slot showed `scopeBadge()` - OPBridge's scope-bundle
   * number. Two things were wrong with that, and the operator found the worse
   * one by looking at a screenshot: a `v`-number rendered against a product
   * wordmark reads as THE PRODUCT'S VERSION to anyone who has not read this
   * file, and the `title` explaining otherwise needs a hover, **which the
   * iPad does not have** - so on the primary device it was undiscoverable.
   *
   * It was also STALE. `scope_bundle_version` is written at pairing time and
   * never refreshed, while OPBridge's `reconcile_scope_bundles_from_defaults()`
   * unions new scopes onto already-issued bearers with no re-pair. MEASURED
   * 2026-09-21: the badge read `v14` while live tokens carried `/obs/scenes`
   * (v15), `/devices/status` (v16) and `/setlist/state` (v17). A latch, not a
   * readback - the same defect this session removed from four other surfaces.
   *
   * **And the thing it looked like did not exist.** Nothing in the app showed
   * its own build, so "is this device running the deploy I just made" could
   * only be answered by curling the served bundle hash from a dev machine.
   * PLAN's headline finding was `CP-145 sat UNDEPLOYED 5 hours while every
   * source gate was green`; the iPad was loading a bundle five hours older
   * than the feature and nothing on screen could have said so.
   *
   * The scope numbers are not deleted - they move into the logo modal, which
   * is a real tap target rather than a hover.
   */
  const buildBadge = createMemo(() => ({
    sha: __BUILD_SHA__,
    dirty: __BUILD_SHA__.endsWith('-dirty'),
    time: __BUILD_TIME__,
  }));


  // Escape closes the transport floater. Navigate (the logo's full-screen
  // launcher) is its own component now and owns its own keys - see
  // components/Navigate.tsx and navigate/store.ts.
  function onFloaterKey(e: KeyboardEvent) {
    if (e.key === 'Escape' && floaterOpen()) setFloaterOpen(false);
  }
  onMount(() => window.addEventListener('keydown', onFloaterKey));
  onCleanup(() => window.removeEventListener('keydown', onFloaterKey));

  return (
    <header class="hdr" data-mode={uiMode()}>
      {/* Left zone: wordmark (opens Navigate) + the clock indicator. The same
          in every state - performance mode changes no view (DEC-216). */}
        <div class="hdr-left">
          {/* The logo opens Navigate, the full-screen launcher (DEC-141;
              rebuilt 2026-09-30 as components/Navigate.tsx - body-mounted,
              adaptive from phone to desktop, with search). The brand mark
              (`public/logo/opbuttons-logo.png`) is a fixed two-tone PNG and
              does NOT retint per skin - a logo is the one place that's
              correct. No caret: a caret means "dropdown" and this opens a
              full-screen surface. The beat dot beside it is .metro-toggle
              below, the wordmark's own clock indicator (2026-08-22). */}
          <button
            type="button"
            class="wordmark wordmark-menu"
            onClick={toggleNavigate}
            data-open={navigateOpen() ? 'true' : 'false'}
            title={navigateDocked()
              ? `${dockHidden() ? 'Show' : 'Hide'} Navigate (\\)${performanceMode() ? ' - performance mode is ON' : ''}`
              : performanceMode() ? 'Navigate - performance mode is ON (Ctrl/Cmd+K)' : 'Navigate - everything in the app (Ctrl/Cmd+K)'}
            aria-label={performanceMode() ? 'Open Navigate (performance mode is on)' : 'Open Navigate'}
            aria-haspopup="dialog"
            aria-expanded={navigateOpen()}
          >
            <img class="wordmark-logo" src="/logo/opbuttons-logo.png" alt="OPButtons" />
            {/* Performance mode indicator (DEC-216, operator's choice): a lit
                dot on the button that opens Navigate, where the toggle lives.
                An indicator, not a change of view - nothing moves. It is here
                because a switch made in another app (OPVisual, the OPBridge
                dashboard) is otherwise invisible from the iPad. */}
            <Show when={performanceMode()}>
              <span class="wordmark-perf-dot" aria-hidden="true" />
            </Show>
            <span
              class="wordmark-scope"
              data-dirty={buildBadge().dirty ? 'true' : 'false'}
              data-update={appUpdateReady() ? 'ready' : 'none'}
              title={appUpdateReady()
                ? `Version ${__APP_VERSION__} (build ${buildBadge().sha}) - a new version is ready. Tap the logo to update.`
                : `Version ${__APP_VERSION__}, built from ${buildBadge().sha}. Settings > About has the build time and details.`}
            >
              {/* The version people see (2026-10-05); the build sha stays in
                  the tooltip and in Settings > About. */}
              v{__APP_VERSION__}
            </span>
          </button>

          {/* Merged clock indicator (2026-08-22, operator idea): idle, this is
              a single dot pulsing in time with the real BPM — same spot the
              old always-static wordmark-dot occupied, now tempo-synced.
              Stopped→started swaps it for the real 4-dot metronome in the
              SAME slot (no crossfade, just Show/fallback — "simple but
              cool" per the operator, not a morph animation), so the resting
              dot reads as "the clock, not started yet" rather than two
              unrelated indicators living in different parts of the header.
              Separate button from the wordmark on purpose — tapping this
              opens the transport floater, tapping the logo opens the menu;
              merging them into one hit-target would make one tap ambiguous. */}
          <button
            type="button"
            ref={metroBtnRef}
            class="metro-toggle"
            data-open={floaterOpen() ? 'true' : 'false'}
            onClick={toggleFloater}
            aria-label={floaterOpen() ? 'Hide transport controls' : 'Show transport controls'}
            aria-expanded={floaterOpen()}
            title={floaterOpen() ? 'Hide transport controls' : 'Show transport controls'}
          >
            <Show
              when={props.isPlaying()}
              fallback={<span class="wordmark-dot" style={{ '--beat-ms': `${idleBeatMs()}ms` }} aria-hidden="true" />}
            >
              <Index each={Array.from({ length: dotCount() })}>
                {(_, i) => (
                  <span class="phase-dot" data-active={i === activeDot() ? 'true' : 'false'} />
                )}
              </Index>
            </Show>
          </button>
          {/* SIZE (DEC-268), on a desktop with Navigate docked: Fill the
              window (the default: the frame, its shape kept, as big as the
              space allows) or 100% (the frame at its real size). The icon
              shows what a press does. Per device; never on the iPad, whose
              canvas is its screen. */}
          <Show when={navigateDocked()}>
            <button
              type="button"
              class="hdr-fill-btn"
              data-size={canvasActualSize() ? 'actual' : 'fill'}
              aria-pressed={canvasActualSize()}
              onClick={() => setCanvasActualSize(!canvasActualSize())}
              title={canvasActualSize() ? 'Fill the window' : '100%: the real size'}
              aria-label={canvasActualSize() ? 'Fill the window with the canvas' : 'Show the canvas at 100%'}
            >
              <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <Show when={canvasActualSize()} fallback={<path d="M6 2.5V6H2.5M13.5 6H10V2.5M10 13.5V10h3.5M2.5 10H6v3.5" />}>
                  <path d="M2.5 6V2.5H6M10 2.5h3.5V6M13.5 10v3.5H10M6 13.5H2.5V10" />
                </Show>
              </svg>
            </button>
          </Show>
        </div>

      {/* Pinned chrome (DEC-170): the bar.beat.sixteenth readout. The dot
          cluster that used to sit here moved into .hdr-left next to the
          wordmark (2026-08-22 — merged with the beat-pulsing logo dot, see
          that block's comment). Everything INTERACTIVE about the transport
          (play/stop, tap-for-beat-1, TAP, sig, BPM drag) still lives in the
          floating panel below, toggled by tapping the moved dots. The bar
          counter stays pinned: it's a readout, not a control, and its own
          drag gesture (the visual-offset calibration) reads more honestly
          next to the thing it's calibrating than off in a panel that can
          move away from it. */}
      <div class="tempo-box">
        <div
          class="bar-counter"
          data-live={transportLive() ? 'true' : 'false'}
          data-dragging={offsetDragging() ? 'true' : 'false'}
          onPointerDown={onOffsetPointerDown}
          title={
            transportLive()
              ? 'Position - bar.beat.sixteenth. Drag up/down to adjust visual offset (compensates for LOM+UDP+WS latency)'
              : 'Position - bar.beat.sixteenth'
          }
        >
          <span class="bar-counter-val">
            <span class="bc-bar">{barNum()}</span>
            <span class="bc-sep">.</span>
            <span class="bc-beat">{beatNum()}</span>
            <span class="bc-sep">.</span>
            <span class="bc-sub">{subNum()}</span>
          </span>
        </div>
        {/* Offset chip — only while dragging or when a calibration is set.
            Rendered OUTSIDE the counter so the counter's width (and therefore
            every digit's position) never moves. */}
        <Show when={transportLive() && (offsetDragging() || transportOffsetMs() !== 0)}>
          <span class="bar-counter-offset" data-dragging={offsetDragging() ? 'true' : 'false'}>
            {transportOffsetMs() > 0 ? '+' : ''}{transportOffsetMs()}ms
          </span>
        </Show>
      </div>

      {/* Floating transport panel (DEC-170). Same shape in both clock states
          so it doesn't restructure when Live connects - play state + beat
          dots + time signature. Live: read-only (Live owns the transport).
          Practice: interactive (play/stop, tap-for-beat-1, tap tempo, cycle
          sig, BPM drag). Body-portalled at measured px (DEC-096 rect-
          anchored-popover exemption), not shellPortal — a dragged position
          is a raw viewport coordinate, not a %-of-shell size. */}
      <Show when={floaterOpen()}>
        <Portal mount={document.body}>
          <div
            class="transport-floater"
            style={{ left: `${effectiveFloaterPos().left}px`, top: `${effectiveFloaterPos().top}px` }}
          >
            {transportLive() ? (
              <div
                class="practice-transport live-transport"
                data-playing={props.transport()?.isPlaying ? 'true' : 'false'}
                title="Live transport - Ableton owns play state and tempo"
              >
                <span class="practice-playstop live-indicator" aria-hidden="true">
                  {props.transport()?.isPlaying ? '▶' : '■'}
                </span>
                <div
                  class="phase-strip live-indicator"
                  data-playing={props.transport()?.isPlaying ? 'true' : 'false'}
                  aria-hidden="true"
                >
                  <Index each={Array.from({ length: dotCount() })}>
                    {(_, i) => (
                      <span class="phase-dot" data-active={i === activeDot() ? 'true' : 'false'} />
                    )}
                  </Index>
                </div>
                <span class="practice-sig live-indicator">
                  {sigNumNow()}/{sigDenNow()}
                </span>
              </div>
            ) : (
              <div class="practice-transport" data-playing={practiceIsPlaying() ? 'true' : 'false'}>
                {/* Play / stop the local practice transport. */}
                <button
                  class="practice-playstop"
                  type="button"
                  onClick={togglePracticePlaying}
                  aria-label={practiceIsPlaying() ? 'Stop practice clock' : 'Start practice clock'}
                  title={practiceIsPlaying() ? 'Stop' : 'Start'}
                >
                  {practiceIsPlaying() ? '■' : '▶'}
                </button>
                {/* Phase strip — beat dots + set-beat-1 tap. Dots track the time
                    signature and stay on display when stopped (the metronome is
                    paused, not gone) — the strip + dots take an amber hue then,
                    driven off data-playing in CSS. */}
                <button
                  class="phase-strip"
                  type="button"
                  onClick={onMetronomeTap}
                  data-playing={practiceIsPlaying() ? 'true' : 'false'}
                  aria-label="Set beat 1"
                  title={practiceIsPlaying() ? 'Tap to set beat 1' : 'Tap to start + set beat 1'}
                >
                  <Index each={Array.from({ length: dotCount() })}>
                    {(_, i) => (
                      <span class="phase-dot" data-active={i === activeDot() ? 'true' : 'false'} />
                    )}
                  </Index>
                </button>
                {/* Tap tempo — 2+ taps set BPM from the average interval. */}
                <button
                  class="practice-tap"
                  type="button"
                  onClick={tapTempo}
                  aria-label="Tap tempo"
                  title="Tap tempo - tap in time to set BPM"
                >
                  TAP
                </button>
                {/* Time signature — click to cycle common meters. */}
                <button
                  class="practice-sig"
                  type="button"
                  onClick={cyclePracticeSig}
                  aria-label="Time signature"
                  title="Time signature - click to cycle"
                >
                  {practiceSigNum()}/{practiceSigDen()}
                </button>
              </div>
            )}

            <button
              class="bpm bpm-drag"
              type="button"
              onPointerDown={onBpmPointerDown}
              data-dragging={dragging() ? 'true' : 'false'}
              data-slaved={props.isSlaved() ? 'true' : 'false'}
              title={
                props.isSlaved()
                  ? 'Slaved to Ableton Live (M4L OPButtons Transport). Tempo follows Live; drag disabled.'
                  : `Drag up/down to change BPM (${BPM_RANGE.min}–${BPM_RANGE.max})`
              }
            >
              {props.isSlaved() ? props.bpm().toFixed(2) : props.bpm().toFixed(0)}
              <small>{props.isSlaved() ? 'live' : 'bpm'}</small>
            </button>

            {/* Grab strip — drag anywhere on screen. Position persists
                per-device (transportFloaterPosition.ts) once dragged. */}
            <span
              class="transport-floater-grab"
              onPointerDown={onFloaterGrabPointerDown}
              title="Drag anywhere"
              aria-hidden="true"
            >
              <i /><i /><i />
            </span>
          </div>
        </Portal>
      </Show>

      {/* Right zone: pages, clock source, bridge pill, edit toggle. The same
          in every state - performance mode changes no view (DEC-216). */}
        <div class="hdr-right">
        {/* EDIT + Add moved to the canvas right-click menu (Edit Page / +
            Item); Navigate reaches both too ("Add a control", search "edit").
            Performance mode, Timelines, Matrix and the rest live in Navigate. */}
        {/* Pages pills — moved up from the (retired) bottom bar. Scrolls
            horizontally if the page count outgrows the space. */}
        <PagesPills />
        {/* Clock-source pill — always-visible readout of the /clock/status
            source (DEC supersede 2026-07-18; was Connect-card-only). Sits
            left of the OPBridge badge since the clock comes FROM the bridge. */}
        <ClockSourcePill isLive={props.isSlaved} />
        {/* OPBridge connectivity pill — moved up from the (retired) bottom
            bar. */}
        <OpbridgeBadge />
        {/* PANIC MOVED OUT OF THIS BAR 2026-09-22 (operator instruction:
            "move panic button to top right of navigate page"). It lives in
            Navigate's header (components/Navigate.tsx), left of the close
            button, in every mode.

            ⚠️ The trade was put to the operator explicitly and they took it:
            the kill switch is now TWO TAPS away (open Navigate, then PANIC)
            and is not visible until Navigate is open, where before it was one
            tap and always on screen in test mode. Recorded here because a
            future session reading only the diff would see a control deleted
            from a stage surface and reasonably try to put it back.

            Performance mode pins no second PANIC anywhere (DEC-216, operator
            2026-09-25): the switch changes no view, this bar stays, and
            Navigate stays one tap away - so on stage it is Navigate then PANIC,
            the same two taps as in rehearsal. */}
        {/* Fit warning, dismissed state. Only rendered once the operator has
            closed the bottom notice, so it is never noise on a rig that fits.
            Sized generously (44px) because on a mismatched project the whole
            header is already scaled down, and this is the one control that
            has to stay hittable in exactly that state. */}
        <Show when={props.fitAlert()}>
          <button
            type="button"
            class="hdr-fit-alert"
            onClick={props.onShowFitNotice}
            title="This project does not fit this screen - tap for options"
            aria-label="Project does not fit this screen"
          >
            !
          </button>
        </Show>
        {/* Undo/redo chrome (2026-08-11). Edit-mode only — SD-2's dead/alive
            list names undo/redo as an editing capability, same bracket as
            the item palette / inspector / page reorder, so these render
            only in edit mode rather than gating just the click handler.
            Disabled state is real (not just dimmed) — `canUndoAny`/
            `canRedoAny` are reactive across EVERY store's history, not
            just whichever editor happens to be open, so the buttons stay
            honest even when nothing on screen looks "undoable" (e.g. the
            last edit was a Matrix device add behind a closed modal).
            `globalUndo`/`globalRedo` are the same entry point the Ctrl+Z
            keyboard handler uses (undo/history.ts) — one code path, two
            triggers, so button and keyboard can't disagree. */}
        <Show when={isEditMode()}>
          <button
            type="button"
            class="hdr-undo-btn"
            disabled={!canUndoAny()}
            onClick={() => globalUndo()}
            title="Undo"
            aria-label="Undo"
          >
            <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
              <path
                d="M4 4.5H10.5A3.5 3.5 0 0 1 14 8A3.5 3.5 0 0 1 10.5 11.5H6"
                fill="none" stroke="currentColor" stroke-width="1.4"
                stroke-linecap="round" stroke-linejoin="round"
              />
              <path
                d="M6.2 2.3L3.3 4.5L6.2 6.7"
                fill="none" stroke="currentColor" stroke-width="1.4"
                stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            class="hdr-redo-btn"
            disabled={!canRedoAny()}
            onClick={() => globalRedo()}
            title="Redo"
            aria-label="Redo"
          >
            <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
              <path
                d="M12 4.5H5.5A3.5 3.5 0 0 0 2 8A3.5 3.5 0 0 0 5.5 11.5H10"
                fill="none" stroke="currentColor" stroke-width="1.4"
                stroke-linecap="round" stroke-linejoin="round"
              />
              <path
                d="M9.8 2.3L12.7 4.5L9.8 6.7"
                fill="none" stroke="currentColor" stroke-width="1.4"
                stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
          </button>
        </Show>
        {/* Edit/Test master toggle (2026-08-11, operator instruction after a
            real bug: a two-finger tap can't know it's two fingers until the
            second finger lands, which is necessarily after the first
            finger's touchdown already fired the control. Operator: "keep it
            simple with edit and perform mode... keep edit behind a master
            button." One tap, no gesture-guessing. Placed immediately left of
            the settings gear per the build plan (.notepads/edit-play-master-
            toggle.md). Adapts DEC-089, doesn't reverse it - the canvas
            right-click "Edit Page" menu item still works, this is just the
            primary route now. Rendered whether performance mode is on or not
            (DEC-216): the switch never locks the performer out. */}
        <button
          type="button"
          class="edit-test-toggle"
          data-on={isEditMode() ? 'true' : 'false'}
          onClick={() => toggleEditMode()}
          title={isEditMode() ? 'Editing - tap to switch to Test' : 'Testing - tap to switch to Edit'}
          aria-label={isEditMode() ? 'Switch to Test mode' : 'Switch to Edit mode'}
        >
          <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
            {/* A pencil, diagonal body + pointed tip + a short line marking
                the ferrule/eraser boundary near the blunt end. Screenshotted
                at 16/24/48/96px against fg-dim / idle / armed (the three
                colours this button actually uses) before committing - reads
                as a pencil at every size and colour tested, not guessed
                (DEC-128's lesson: icons must be looked at, not assumed). */}
            <g fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2.67 13.33h2.67L12.33 6.33a1.885 1.885 0 1 0 -2.67 -2.67l-7 7v2.67Z" />
              <path d="M9 4.33l2.67 2.67" />
            </g>
          </svg>
        </button>
        {/* Settings gear RETIRED from the bar (DEC-170). Settings is a card in
            Navigate, which can also land on one section directly
            (`openSettings('export')` for Backup, 'interface' for Look). */}
        {/* Live's transport, top right (DEC-322): Playing / Paused / Stopped,
            or No transport without a fresh report. */}
        <TransportPill state={() => transportState(props.transport(), transportLive())} />
        </div>
    </header>
  );
}
